export type ActivityChatAdventure = {
  id: string;
  user_id: string;
  title: string;
  location?: string | null;
  date_start: string;
  date_end?: string | null;
  created_at?: string | null;
  chat_last_message_at?: string | null;
};

export type ActivityChatMembership = {
  adventure_id: string;
  user_id: string;
  status: string;
  created_at?: string | null;
};

export type ActivityChatMessage = {
  adventure_id: string;
  content?: string | null;
  created_at: string;
  user_id?: string | null;
  profiles?:
    | { display_name?: string | null }
    | Array<{ display_name?: string | null }>
    | null;
};

export type ActivityChatUnreadCount = {
  conversation_id: string;
  unread_count: number;
};

export type ActivityChatListItem = {
  id: string;
  activityTitle: string;
  title: string;
  location: string | null;
  dateStart: string;
  dateEnd: string | null;
  createdAt: string | null;
  memberCount: number;
  isOrganiser: boolean;
  isJoined: boolean;
  lastActivityAt: string | null;
  preview: string;
  unreadCount: number;
};

const parseDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatActivityChatDate = (value: string) => {
  const date = parseDate(value);
  if (!date) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

export const formatActivityChatDateRange = (dateStart: string, dateEnd?: string | null) => {
  const startLabel = formatActivityChatDate(dateStart);
  if (!dateEnd || dateEnd === dateStart) return startLabel;
  return `${startLabel} - ${formatActivityChatDate(dateEnd)}`;
};

export const buildActivityChatTitle = (
  activityTitle: string,
  dateStart: string,
  dateEnd?: string | null,
) => `${activityTitle.trim()} · ${formatActivityChatDateRange(dateStart, dateEnd)}`;

const getMessageActorName = (message: ActivityChatMessage) => {
  const profile = Array.isArray(message.profiles) ? message.profiles[0] : message.profiles;
  return profile?.display_name?.trim() || null;
};

const getSortTimestamp = (item: ActivityChatListItem) =>
  parseDate(item.lastActivityAt)?.getTime() ?? parseDate(item.createdAt)?.getTime() ?? 0;

export const buildActivityChatList = ({
  adventures,
  memberships,
  messages,
  unreadCounts = [],
  currentUserId,
}: {
  adventures: ActivityChatAdventure[];
  memberships: ActivityChatMembership[];
  messages: ActivityChatMessage[];
  unreadCounts?: ActivityChatUnreadCount[];
  currentUserId: string;
}): ActivityChatListItem[] => {
  const membershipsByAdventure = new Map<string, ActivityChatMembership[]>();
  for (const membership of memberships) {
    const current = membershipsByAdventure.get(membership.adventure_id) ?? [];
    current.push(membership);
    membershipsByAdventure.set(membership.adventure_id, current);
  }

  const latestMessageByAdventure = new Map<string, ActivityChatMessage>();
  const unreadCountByAdventure = new Map(
    unreadCounts.map((entry) => [entry.conversation_id, entry.unread_count]),
  );
  for (const message of messages) {
    const current = latestMessageByAdventure.get(message.adventure_id);
    if (!current || new Date(message.created_at).getTime() > new Date(current.created_at).getTime()) {
      latestMessageByAdventure.set(message.adventure_id, message);
    }
  }

  return adventures
    .map((adventure) => {
      const adventureMemberships = membershipsByAdventure.get(adventure.id) ?? [];
      const isJoined = adventureMemberships.some(
        (membership) => membership.user_id === currentUserId && membership.status === "joined",
      );
      const joinedMemberIds = new Set(
        adventureMemberships
          .filter((membership) => membership.status === "joined")
          .map((membership) => membership.user_id),
      );
      joinedMemberIds.add(adventure.user_id);

      const latestMessage = latestMessageByAdventure.get(adventure.id);
      const actorName = latestMessage ? getMessageActorName(latestMessage) : null;
      const messagePreview = latestMessage?.content?.trim();

      return {
        id: adventure.id,
        activityTitle: adventure.title,
        title: buildActivityChatTitle(adventure.title, adventure.date_start, adventure.date_end),
        location: adventure.location?.trim() || null,
        dateStart: adventure.date_start,
        dateEnd: adventure.date_end ?? null,
        createdAt: adventure.created_at ?? null,
        memberCount: joinedMemberIds.size,
        isOrganiser: adventure.user_id === currentUserId,
        isJoined,
        lastActivityAt: latestMessage?.created_at ?? adventure.chat_last_message_at ?? null,
        preview: messagePreview
          ? `${actorName ? `${actorName}: ` : ""}${messagePreview}`
          : adventure.location?.trim()
            ? `Planning chat for ${adventure.location.trim()}`
            : "Activity planning chat",
        unreadCount: unreadCountByAdventure.get(adventure.id) ?? 0,
      } satisfies ActivityChatListItem;
    })
    .sort((left, right) => getSortTimestamp(right) - getSortTimestamp(left));
};
